<template>

  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-if="canAddStudent(researchAdvisor)"
            v-b-toggle.sidebar-add-student
            variant="primary"
          >
            {{ $t('Add student') }}
          </b-button>

        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('Search')"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="studentTable"
      :items="list"
      responsive
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      :empty-text="$t('No matching records found')"
      :sort-desc.sync="isSortDirDesc"
      :busy="isBusy"
      class="position-relative"
    >
      <template #head()="data">
        {{ $t(data.label) }}
      </template>

      <template #cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <template #cell(dc_type)="data">
        {{ $langName(getDcType(data.value).name) }}
      </template>
      <template #cell(organization)="data">
        {{ $langName(data.value.name) }}
      </template>

      <template #cell(user)="data">
        {{ data.value.full_name }}
      </template>

      <template #cell(edu_lang)="data">
        {{ $langName(getEduLang(data.value).name) }}
      </template>

      <template #cell(quarter)="data">
        {{ data.value.period.admission_year + '/' +data.value.quarter }}
      </template>

      <template #cell(organization)="data">
        {{ $langName(data.value.name) }}
      </template>

      <template #cell(direction)="data">
        {{ data.value.code + ' - ' + $langName(data.value.name) }}
      </template>

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            variant="primary"
            label=""
          />
        </div>
      </template>

      <template #cell(status)="data">
        <b-badge :variant="getApplyDocumentStatusColor(data.value)">
          {{ $langName(getApplyDocumentStatus(data.value).name) }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <feather-icon
            icon="EyeIcon"
            class="cursor-pointer mr-1"
            size="16"
            @click="$router.push({ name: 'apply-document-view', params: { id: data.item.id }})"
          />
          <feather-icon
            v-if="canUpdateStudent(data.item)"
            v-b-toggle.sidebar-update-student
            icon="EditIcon"
            class="cursor-pointer mr-1"
            size="16"
            @click="update(data.item)"
          />
          <feather-icon
            icon="TrashIcon"
            class="cursor-pointer mr-1"
            size="16"
            @click="remove(data.item)"
          />
        </div>
      </template>

    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">{{ $t("table summary", { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }} </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>
      </b-row>
      <add
        ref="addStudent"
        @refetchData="refetchData"
      />
      <update-student
        ref="updateStudent"
        @refetchData="refetchData"
      />
    </div>
  </b-card>

</template>

<script>
import {
  BButton, BCard, BCol, BFormInput, BPagination, BRow, BSpinner, BTable, VBToggle, BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import config from './config'
import studentModule from '@/store/student/index'
import Add from './Add.vue'
import permission from '@/views/pages/research-advisor/permission'
import UpdateStudent from '@/views/pages/research-advisor/advisor-students/UpdateStudent.vue'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    UpdateStudent,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    vSelect,
    BSpinner,
    BBadge,
    Add,
  },
  computed: {
    researchAdvisor() {
      return this.$store.state.researchAdvisor.researchAdvisor
    },
  },
  mounted() {
    this.$store.commit(`${this.MODULE_NAME}/FRESH`)
    this.$store.dispatch('staticModule/applyDocumentStatuses')
    this.extraParams = { advisor_id: this.researchAdvisor.user.id }
  },
  methods: {
    getDcType(id) {
      return this.$store.getters['staticModule/getDcType'](id)
    },
    getEduLang(id) {
      return this.$store.getters['staticModule/getEduLangType'](id)
    },
    getApplyDocumentStatus(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatus'](id)
    },
    getApplyDocumentStatusColor(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatusColor'](id)
    },
    update(data) {
      this.$refs.updateStudent.fetchAdvisor('', () => {})
      this.$refs.updateStudent.selectedAdvisor = data.advisor
      this.$store.commit('researchAdvisor/UPDATE_STUDENT', { id: data.id, advisor_id: data.advisor.id })
    },
    remove(applyDocument) {
      const data = {
        id: applyDocument.id,
        advisorId: this.researchAdvisor.user.id,
      }
      this.$removeSwal(this, data, 'researchAdvisor/delStudent', this.refetchData)
    },
  },
  setup() {
    const MODULE_NAME = 'student'
    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, studentModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    return {
      ...permission(),
      MODULE_NAME,
      ...config(),
    }
  },
}
</script>
